/**
 * Plain-English schedules. The assistant reads cron times in its own time zone (the person's choice in
 * Settings; UTC until they choose one), so times are converted from that zone to the viewer's own —
 * with the assistant on UTC, "30 3 * * *" reads "Every day at 9:00 AM" in India.
 */
import { canonicalTimeZone } from "@krut/shared";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Minutes `zone` is ahead of UTC at that instant. */
function offsetMinutes(zone: string, at: number): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: zone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric" })
      .formatToParts(at)
      .map((x) => [x.type, Number(x.value)]),
  );
  return (Date.UTC(p.year!, p.month! - 1, p.day!, p.hour!, p.minute!) - Math.floor(at / 60_000) * 60_000) / 60_000;
}

function localTime(hour: number, minute: number, zone: string) {
  const wall = Date.UTC(2026, 0, 5, hour, minute); // a Monday, so day shifts are visible
  let at = wall;
  try {
    at = wall - offsetMinutes(zone, wall) * 60_000;
  } catch {
    /* unknown zone: treat as UTC */
  }
  const d = new Date(at);
  return { text: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }), dayShift: d.getDay() - 1 };
}

/** The viewer's own time zone as the assistant names it (e.g. "Asia/Kolkata", which Chrome calls "Asia/Calcutta"). */
export const browserZone = () => canonicalTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC") ?? "UTC";

/** null = every day; false = a pattern this doesn't describe. */
function days(field: string, shift: number): string | null | false {
  if (field === "*") return null;
  const list: number[] = [];
  for (const part of field.split(",")) {
    const r = /^(\d)(?:-(\d))?$/.exec(part);
    if (!r) return false;
    const [a, b] = [Number(r[1]), Number(r[2] ?? r[1])];
    for (let i = a; i <= b; i++) list.push(((i % 7) + shift + 7) % 7);
  }
  const set = [...new Set(list)].sort();
  if (set.join() === "1,2,3,4,5") return "every weekday";
  if (set.join() === "0,6") return "every weekend";
  return "every " + set.map((d) => DAYS[d]).join(", ");
}

/** `zone`: the time zone the assistant reads cron in (null = UTC). */
export function describeSchedule(expr: string | undefined, zone: string | null = null): string {
  if (!expr) return "";
  const e = expr.trim();
  const every = /^every\s+(\d+)\s*([mhd])$/i.exec(e);
  if (every) return `Every ${every[1]} ${{ m: "minutes", h: "hours", d: "days" }[every[2]!.toLowerCase() as "m" | "h" | "d"]}`;
  const f = e.split(/\s+/);
  if (f.length !== 5) return e;
  const [min, hour, dom, mon, dow] = f as [string, string, string, string, string];
  if (/^\*\/\d+$/.test(min) && hour === "*" && dom === "*" && mon === "*" && dow === "*") return `Every ${min.slice(2)} minutes`;
  if (/^\d+$/.test(min) && /^\*\/\d+$/.test(hour) && dom === "*" && mon === "*" && dow === "*") return `Every ${hour.slice(2)} hours`;
  if (/^\d+$/.test(min) && hour === "*" && dom === "*" && mon === "*" && dow === "*") return `Every hour at :${min.padStart(2, "0")}`;
  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour) || mon !== "*") return e;
  const t = localTime(Number(hour), Number(min), zone ?? "UTC");
  if (dom === "*") {
    const d = days(dow, t.dayShift);
    if (d === false) return e;
    return `${d ? d[0]!.toUpperCase() + d.slice(1) : "Every day"} at ${t.text}`;
  }
  if (/^\d+$/.test(dom) && dow === "*") return `Monthly on day ${dom} at ${t.text}`;
  return e;
}

export const relative = (iso: string | null | undefined) => {
  if (!iso) return "—";
  const ms = new Date(iso).getTime() - Date.now();
  const abs = Math.abs(ms);
  const fmt = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (abs < 60_000) return fmt.format(Math.round(ms / 1000), "second");
  if (abs < 3_600_000) return fmt.format(Math.round(ms / 60_000), "minute");
  if (abs < 86_400_000) return fmt.format(Math.round(ms / 3_600_000), "hour");
  return fmt.format(Math.round(ms / 86_400_000), "day");
};
